import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import api from '../services/api';
import Select from 'react-select';
import { useAcademicYear } from '../contexts/AcademicYearContext';

const OuvertureClasseModal = ({ show, onClose, editing, classes, annees, enseignements = [], niveaux = [] }) => {
  const { selectedYear } = useAcademicYear();

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    defaultValues: {
      classe_id: '',
      annee_scolaire_id: selectedYear?.id || '',
      enseignement_id: '',
      niveau_id: '',
      date_ouverture: '',
      part_evaluation: 0.6,
      part_composition: 0.4,
    }
  });

  // Mettre à jour le formulaire quand editing change
  useEffect(() => {
    if (editing) {
      reset({
        classe_id: editing.classe_id || '',
        annee_scolaire_id: editing.annee_scolaire_id || selectedYear?.id || '',
        enseignement_id: editing.enseignement_id || '',
        niveau_id: editing.niveau_id || '',
        date_ouverture: editing.date_ouverture || '',
        part_evaluation: editing.part_evaluation || 0.6,
        part_composition: editing.part_composition || 0.4,
      });
    } else {
      reset({
        classe_id: '',
        annee_scolaire_id: selectedYear?.id || '',
        enseignement_id: '',
        niveau_id: '',
        date_ouverture: '',
        part_evaluation: 0.6,
        part_composition: 0.4,
      });
    }
  }, [editing, reset, selectedYear]);

  const onSubmit = async (data) => {
    try {
      if (editing) {
        // Utiliser ouverture_classe_id ou id selon ce qui est disponible
        const id = editing.ouverture_classe_id || editing.id;
        await api.put(`/ouvertures-classes/${id}`, data);
      } else {
        await api.post('/ouvertures-classes', data, {
           headers: { 'Content-Type': 'application/json' }
        });
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      throw error;
    }
  };

  if (!show) return null;

  // Options pour react-select
  const anneeOptions = annees.map(a => ({
    value: a.id,
    label: a.annee_scolaire || a.libelle || a.nom || a.id
  }));
  const classeOptions = classes.map(c => ({
    value: c.id,
    label: typeof c.classe === 'string' ? c.classe : (c.classe?.classe || c.libelle || c.nom || String(c.id))
  }));
  const enseignementOptions = enseignements.map(e => ({
    value: e.id,
    label: e.enseignement || e.description || e.libelle || e.nom || e.id
  }));
  const niveauOptions = niveaux.map(n => ({
    value: n.id,
    label: n.niveau || n.description || n.libelle || n.nom || n.id
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {editing ? 'Modifier' : 'Ajouter une'} ouverture de classe
          </h2>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année scolaire *</label>
              <Controller
                name="annee_scolaire_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={anneeOptions}
                    value={anneeOptions.find(opt => opt.value === field.value)}
                    onChange={option => field.onChange(option ? option.value : '')}
                    placeholder="Sélectionner..."
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.annee_scolaire_id && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe *</label>
              <Controller
                name="classe_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={classeOptions}
                    value={classeOptions.find(opt => opt.value === field.value)}
                    onChange={option => field.onChange(option ? option.value : '')}
                    placeholder="Sélectionner..."
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.classe_id && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enseignement *</label>
              <Controller
                name="enseignement_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={enseignementOptions}
                    value={enseignementOptions.find(opt => opt.value === field.value)}
                    onChange={option => field.onChange(option ? option.value : '')}
                    placeholder="Sélectionner..."
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.enseignement_id && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niveau *</label>
              <Controller
                name="niveau_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={niveauOptions}
                    value={niveauOptions.find(opt => opt.value === field.value)}
                    onChange={option => field.onChange(option ? option.value : '')}
                    placeholder="Sélectionner..."
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.niveau_id && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date d'ouverture</label>
              <input type="date" {...register('date_ouverture')} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Part évaluation</label>
                <input type="number" step="0.01" min="0" max="1" {...register('part_evaluation')} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Part composition</label>
                <input type="number" step="0.01" min="0" max="1" {...register('part_composition')} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded-lg" onClick={onClose}>Annuler</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg">Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OuvertureClasseModal;
